import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../hooks/useLanguage';
import { content } from '../data/content';
import { Button } from './ui/Button';
import { Icon } from './ui/Icon';
import { Reveal } from './ui/Reveal';
import { Eyebrow } from './ui/Section';

const easing = [0.16, 1, 0.3, 1];

const HeroVisual = ({ statValues, ticker }) => (
  <div className="relative">
    {/* Backdrop frame */}
    <div className="absolute -inset-6 bg-radial-fade opacity-60 blur-2xl rounded-[3rem]" />
    <div
      className="relative rounded-3xl border bg-surface shadow-lift overflow-hidden"
      style={{ borderColor: 'var(--border)' }}
    >
      {/* Faux window chrome */}
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: '#ff5f56' }} />
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: '#ffbd2e' }} />
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: '#27c93f' }} />
        </div>
        <div className="text-mono text-[0.7rem] text-subtle">emfa › operations.dashboard</div>
        <div className="h-2.5 w-2.5 rounded-full bg-elev" />
      </div>

      {/* Dashboard mock */}
      <div className="p-5 sm:p-6 grid gap-4">
        {/* KPI strip */}
        <div className="grid grid-cols-2 gap-3">
          {statValues.slice(0, 4).map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 + i * 0.06, ease: easing }}
              className="rounded-xl border bg-elev p-3"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="flex items-center justify-between">
                <span className="text-mono text-[0.65rem] uppercase tracking-[0.18em] text-subtle">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="h-1.5 w-1.5 rounded-full bg-signal-mint animate-pulse-ring" />
              </div>
              <div className="mt-2 text-display text-xl font-bold text-app">{s.value}</div>
              <div className="text-[0.65rem] text-muted mt-0.5 leading-tight line-clamp-2">{s.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Faux chart */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.6 }}
          className="rounded-xl border bg-elev p-4"
          style={{ borderColor: 'var(--border)' }}
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-app">Estate health · 30 days</p>
            <div className="flex items-center gap-2 text-mono text-[0.65rem] text-subtle">
              <span className="h-2 w-2 rounded-sm" style={{ background: 'var(--brand)' }} />
              <span>uptime</span>
              <span className="h-2 w-2 rounded-sm ml-2" style={{ background: 'var(--accent)' }} />
              <span>latency</span>
            </div>
          </div>
          <svg viewBox="0 0 300 90" className="w-full h-24" preserveAspectRatio="none">
            <defs>
              <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--brand)" stopOpacity="0.45" />
                <stop offset="100%" stopColor="var(--brand)" stopOpacity="0" />
              </linearGradient>
            </defs>
            <motion.path
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 1.4, delay: 0.8, ease: easing }}
              d="M0,60 C30,50 50,42 80,46 C110,50 130,30 165,28 C195,26 220,40 250,30 C275,22 290,18 300,16"
              fill="none"
              stroke="var(--brand)"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <motion.path
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.4 }}
              d="M0,60 C30,50 50,42 80,46 C110,50 130,30 165,28 C195,26 220,40 250,30 C275,22 290,18 300,16 L300,90 L0,90 Z"
              fill="url(#g1)"
            />
            <motion.path
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.4, delay: 1, ease: easing }}
              d="M0,70 C20,68 40,62 70,64 C100,66 125,55 160,52 C190,50 220,55 250,48 C275,42 290,38 300,36"
              fill="none"
              stroke="var(--accent)"
              strokeWidth="1.6"
              strokeDasharray="3 3"
            />
          </svg>
        </motion.div>

        {/* Operations log */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1 }}
          className="rounded-xl border bg-elev divide-y"
          style={{ borderColor: 'var(--border)' }}
        >
          {[
            { label: 'Backup verified · 12 hosts', tag: 'OK' },
            { label: 'Hyper-V patch deployed', tag: 'SHIPPED' },
            { label: 'Quarterly access review', tag: 'NEXT' },
          ].map((row, idx) => (
            <div key={row.label} className="flex items-center justify-between px-4 py-2.5 text-xs"
                 style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center gap-2">
                <span className="text-mono text-subtle">{String(idx + 1).padStart(2, '0')}</span>
                <span className="text-app">{row.label}</span>
              </div>
              <span className="text-mono text-[0.6rem] tracking-widest text-subtle">{row.tag}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </div>

    {/* Ticker */}
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.2, duration: 0.6 }}
      className="absolute -bottom-5 left-6 right-6 rounded-2xl px-4 py-2 flex items-center gap-2 bg-app shadow-card border"
      style={{ borderColor: 'var(--border)' }}
    >
      <span className="h-2 w-2 rounded-full bg-signal-mint animate-pulse" />
      <p className="text-xs text-muted truncate">{ticker}</p>
    </motion.div>
  </div>
);

export const Hero = () => {
  const { language } = useLanguage();
  const t = content[language];

  return (
    <section
      id="home"
      className="relative pt-28 sm:pt-32 md:pt-40 pb-16 sm:pb-24 overflow-hidden bg-app"
    >
      {/* Background grid + radial accent */}
      <div className="absolute inset-0 bg-linegrid opacity-60 pointer-events-none [mask-image:radial-gradient(ellipse_70%_60%_at_50%_30%,#000_30%,transparent_80%)]" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 50% 40% at 50% 10%, color-mix(in oklab, var(--brand) 22%, transparent), transparent 70%)',
        }}
      />

      <div className="container relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7">
            <Reveal>
              <Eyebrow label={t.hero.eyebrow} />
            </Reveal>
            <Reveal delay={80}>
              <h1 className="mt-4 sm:mt-5 text-display font-bold text-display-lg sm:text-display-xl lg:text-display-2xl text-app break-words hyphens-auto">
                {t.hero.title}
              </h1>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-3 text-display text-display-md sm:text-display-md text-gradient-brand bg-[length:200%_100%] animate-gradient-pan break-words hyphens-auto">
                {t.hero.titleAccent}
              </p>
            </Reveal>
            <Reveal delay={240}>
              <p className="mt-5 sm:mt-7 text-base sm:text-lg text-muted leading-relaxed max-w-2xl break-words hyphens-auto">
                {t.hero.subtitle}
              </p>
            </Reveal>

            <Reveal delay={320}>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  variant="primary"
                  icon={<Icon name="arrowRight" size={16} />}
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  {t.hero.cta}
                </Button>
                <Button
                  size="lg"
                  variant="ghost"
                  icon={<Icon name="arrowRight" size={16} />}
                  onClick={() => document.getElementById('process')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  {t.hero.ctaSecondary}
                </Button>
              </div>
            </Reveal>

            <Reveal delay={400}>
              <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 max-w-2xl">
                {t.hero.stats.map((s) => (
                  <div key={s.label} className="border-l pl-4" style={{ borderColor: 'var(--border-strong)' }}>
                    <div className="text-display text-xl sm:text-2xl font-bold text-app">{s.value}</div>
                    <div className="text-xs text-muted mt-1 leading-tight">{s.label}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Right: visual dashboard */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: easing }}
            >
              <HeroVisual statValues={t.hero.stats} ticker={t.hero.ticker} />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
